import {
  Application,
  CanvasRenderer,
  Container,
  Stage,
  Text,
  UIBaseElement,
  WebGLRenderer,
  state,
  video,
  type Camera2d
} from "melonjs";
import { computeP0Layout } from "@h5-rpg/app-shell";
import type {
  ContentBundle,
  ControllerSnapshot,
  GameAction,
  GameControllerPort,
  LifecycleProbePort,
  MountedRuntime,
  P0Layout,
  Rect,
  SceneId
} from "@h5-rpg/contracts";

type DrawRenderer = CanvasRenderer | WebGLRenderer;

export type RuntimeUiMount = (
  application: unknown,
  options: {
    controller: GameControllerPort;
    content: ContentBundle;
    width: number;
    height: number;
    probe?: LifecycleProbePort;
  }
) => () => void;

export type P0RuntimeOptions = {
  target: HTMLElement;
  controller: GameControllerPort;
  content: ContentBundle;
  probe?: LifecycleProbePort;
  mountUi?: RuntimeUiMount;
};

class DynamicText extends Text {
  private current = "";

  constructor(
    x: number,
    y: number,
    private readonly getter: () => string,
    settings: {
      size: number;
      color?: string;
      align?: "left" | "center" | "right";
      anchor?: string;
      bold?: boolean;
      wrap?: number;
    }
  ) {
    super(x, y, {
      font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      size: settings.size,
      fillStyle: settings.color ?? "#f7f1e6",
      textAlign: settings.align ?? "left",
      anchorPoint: settings.anchor ?? "top-left",
      wordWrapWidth: settings.wrap,
      text: ""
    });
    this.floating = false;
    this.alwaysUpdate = true;
    if (settings.bold) {
      this.bold();
    }
    this.refresh();
  }

  override update(): boolean {
    return this.refresh();
  }

  private refresh(): boolean {
    const next = this.getter();
    if (next === this.current) {
      return false;
    }
    this.current = next;
    this.setText(next);
    return true;
  }
}

const drawPanel = (
  renderer: DrawRenderer,
  rect: Rect,
  fill: string,
  stroke = "#6f86a8"
): void => {
  renderer.setColor(fill);
  renderer.fillRoundRect(rect.x, rect.y, rect.width, rect.height, 18);
  renderer.setColor(stroke);
  renderer.strokeRoundRect(rect.x, rect.y, rect.width, rect.height, 18);
};

const drawButton = (
  renderer: DrawRenderer,
  rect: Rect,
  active = true
): void => {
  renderer.setColor(active ? "#df6b47" : "#4a5363");
  renderer.fillRoundRect(rect.x, rect.y, rect.width, rect.height, 14);
  renderer.setColor(active ? "#ffc27b" : "#707887");
  renderer.strokeRoundRect(rect.x, rect.y, rect.width, rect.height, 14);
};

const drawHpBar = (
  renderer: DrawRenderer,
  x: number,
  y: number,
  width: number,
  value: number,
  max: number,
  color: string
): void => {
  renderer.setColor("#111827");
  renderer.fillRoundRect(x, y, width, 16, 8);
  renderer.setColor(color);
  renderer.fillRoundRect(
    x + 2,
    y + 2,
    Math.max(0, (width - 4) * (value / max)),
    12,
    6
  );
};

const drawHero = (
  renderer: DrawRenderer,
  x: number,
  y: number,
  scale: number
): void => {
  renderer.setColor("#5cd6ff33");
  renderer.fillArc(x, y, 50 * scale, 0, Math.PI * 2);
  renderer.setColor("#f1d0bd");
  renderer.fillArc(x, y - 26 * scale, 18 * scale, 0, Math.PI * 2);
  renderer.setColor("#20253a");
  renderer.fillArc(x - 4 * scale, y - 32 * scale, 20 * scale, Math.PI, Math.PI * 2);
  renderer.setColor("#466eac");
  renderer.fillRoundRect(
    x - 27 * scale,
    y - 10 * scale,
    54 * scale,
    68 * scale,
    18 * scale
  );
  renderer.setColor("#dcecff");
  renderer.fillLine(
    x - 35 * scale,
    y + 42 * scale,
    x + 38 * scale,
    y - 12 * scale
  );
};

const drawEnemy = (
  renderer: DrawRenderer,
  x: number,
  y: number,
  scale: number,
  defeated: boolean
): void => {
  renderer.setColor(defeated ? "#59627355" : "#d9536f22");
  renderer.fillArc(x, y, 58 * scale, 0, Math.PI * 2);
  renderer.setColor(defeated ? "#5e6674" : "#552c55");
  renderer.fillRoundRect(
    x - 42 * scale,
    y - 25 * scale,
    84 * scale,
    78 * scale,
    34 * scale
  );
  renderer.setColor(defeated ? "#808792" : "#e89bc2");
  renderer.fillArc(x - 18 * scale, y - 8 * scale, 7 * scale, 0, Math.PI * 2);
  renderer.fillArc(x + 18 * scale, y - 8 * scale, 7 * scale, 0, Math.PI * 2);
  renderer.setColor(defeated ? "#646b75" : "#8b416d");
  renderer.fillRoundRect(
    x - 22 * scale,
    y + 17 * scale,
    44 * scale,
    9 * scale,
    5 * scale
  );
};

class SceneSurface extends UIBaseElement {
  private effectMs = 0;
  private seenRevision = -1;
  private readonly releaseProbe: (() => void) | undefined;

  constructor(
    private readonly scene: SceneId,
    private readonly controller: GameControllerPort,
    private readonly content: ContentBundle,
    private readonly logicalWidth: number,
    private readonly logicalHeight: number,
    probe?: LifecycleProbePort
  ) {
    super(0, 0, logicalWidth, logicalHeight);
    this.anchorPoint.set(0, 0);
    this.isClickable = false;
    this.alwaysUpdate = true;
    this.releaseProbe = probe?.register("sceneSurface");
    this.createLabels();
  }

  override update(dt: number): boolean {
    const snapshot = this.controller.getSnapshot();
    if (snapshot.revision !== this.seenRevision) {
      this.seenRevision = snapshot.revision;
      if (snapshot.events.some((event) => event.type === "DamageApplied")) {
        this.effectMs = 420;
      }
    }
    if (this.effectMs > 0) {
      this.effectMs = Math.max(0, this.effectMs - dt);
    }
    return super.update(dt) || this.effectMs > 0;
  }

  override draw(
    renderer: DrawRenderer,
    viewport?: Camera2d
  ): void {
    const snapshot = this.controller.getSnapshot();
    const map = this.content.maps[snapshot.state.mapId];
    if (!map) {
      return;
    }
    const layout = computeP0Layout(
      this.logicalWidth,
      this.logicalHeight,
      map.width,
      map.height,
      snapshot.state.position
    );
    renderer.setColor("#090e19");
    renderer.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    if (this.scene === "title") {
      this.drawTitle(renderer, layout);
    } else if (this.scene === "map") {
      this.drawMap(renderer, layout, snapshot);
    } else if (this.scene === "battle") {
      this.drawBattle(renderer, layout, snapshot);
    } else {
      this.drawResult(renderer, layout);
    }
    super.draw(renderer, viewport);
  }

  override destroy(): void {
    this.releaseProbe?.();
    super.destroy();
  }

  private createLabels(): void {
    const get = () => this.controller.getSnapshot();
    const mapTitle = () => {
      const state = get().state;
      return this.content.maps[state.mapId]?.title ?? state.mapId;
    };
    const map = this.content.maps[get().state.mapId];
    if (!map) {
      return;
    }
    const layout = computeP0Layout(
      this.logicalWidth,
      this.logicalHeight,
      map.width,
      map.height,
      get().state.position
    );
    const add = (
      x: number,
      y: number,
      getter: () => string,
      settings: ConstructorParameters<typeof DynamicText>[3],
      z = 20
    ) => this.addChild(new DynamicText(x, y, getter, settings), z);

    if (this.scene === "title") {
      add(36, 34, () => "H5 RPG FRAMEWORK · P0", {
        size: layout.mode === "portrait" ? 16 : 18,
        color: "#8eb5df",
        bold: true
      });
      add(this.logicalWidth / 2, this.logicalHeight * 0.34, () => "流光试炼", {
        size: layout.mode === "portrait" ? 48 : 58,
        align: "center",
        anchor: "center",
        bold: true
      });
      add(
        this.logicalWidth / 2,
        this.logicalHeight * 0.44,
        () => "两张地图 · 手动回合 · 同一游戏循环",
        {
          size: layout.mode === "portrait" ? 18 : 21,
          color: "#aebdd0",
          align: "center",
          anchor: "center"
        }
      );
      add(
        layout.primaryButton.x + layout.primaryButton.width / 2,
        layout.primaryButton.y + layout.primaryButton.height / 2,
        () => "开始本地试玩",
        {
          size: 22,
          align: "center",
          anchor: "center",
          bold: true
        }
      );
      return;
    }

    add(24, layout.mode === "portrait" ? 22 : 18, mapTitle, {
      size: layout.mode === "portrait" ? 26 : 28,
      bold: true
    });
    add(
      layout.mode === "portrait" ? 24 : this.logicalWidth - 276,
      layout.mode === "portrait" ? 61 : 22,
      () => `生命 ${get().state.player.hp} / ${get().state.player.maxHp}`,
      { size: 17, color: "#a7d8ff" }
    );

    if (this.scene === "map") {
      const infoX =
        layout.mode === "portrait"
          ? 24
          : layout.mapViewport.x + layout.mapViewport.width + 28;
      const infoY = layout.mode === "portrait" ? this.logicalHeight - 188 : 102;
      add(
        infoX,
        infoY,
        () =>
          layout.mode === "portrait"
            ? "点击相邻格或滑动移动"
            : "移动\nWASD / 方向键\n点击相邻格\n\n目标\n击败守卫后穿过传送门",
        {
          size: layout.mode === "portrait" ? 17 : 19,
          color: "#b9c7d8",
          wrap: layout.mode === "portrait" ? this.logicalWidth - 48 : 240
        }
      );
      add(
        layout.menuButton.x + layout.menuButton.width / 2,
        layout.menuButton.y + layout.menuButton.height / 2,
        () => "≡",
        { size: 28, align: "center", anchor: "center", bold: true }
      );
      return;
    }

    if (this.scene === "battle") {
      add(
        layout.enemyArea.x + layout.enemyArea.width / 2,
        layout.enemyArea.y + 18,
        () => get().state.battle?.enemyName ?? "敌人",
        {
          size: 23,
          align: "center",
          anchor: "top",
          bold: true
        }
      );
      add(
        layout.enemyArea.x + layout.enemyArea.width / 2,
        layout.enemyArea.y + 50,
        () => {
          const battle = get().state.battle;
          return battle ? `${battle.enemyHp} / ${battle.enemyMaxHp}` : "";
        },
        {
          size: 16,
          color: "#f0a9b5",
          align: "center",
          anchor: "top"
        }
      );
      add(
        layout.playerArea.x + layout.playerArea.width / 2,
        layout.playerArea.y + 16,
        () => "试炼者",
        {
          size: 21,
          align: "center",
          anchor: "top",
          bold: true
        }
      );
      add(
        layout.primaryButton.x + layout.primaryButton.width / 2,
        layout.primaryButton.y + layout.primaryButton.height / 2,
        () => (get().state.battle?.won ? "查看战利品" : "普通攻击"),
        {
          size: 21,
          align: "center",
          anchor: "center",
          bold: true
        }
      );
      add(
        layout.menuButton.x + layout.menuButton.width / 2,
        layout.menuButton.y + layout.menuButton.height / 2,
        () => "≡",
        { size: 28, align: "center", anchor: "center", bold: true }
      );
      return;
    }

    add(
      this.logicalWidth / 2,
      this.logicalHeight * 0.25,
      () => "战斗胜利",
      {
        size: layout.mode === "portrait" ? 38 : 44,
        align: "center",
        anchor: "center",
        bold: true
      }
    );
    add(
      this.logicalWidth / 2,
      this.logicalHeight * 0.46,
      () => "试炼徽记  × 1",
      {
        size: 26,
        color: "#ffd27a",
        align: "center",
        anchor: "center",
        bold: true
      }
    );
    add(
      layout.primaryButton.x + layout.primaryButton.width / 2,
      layout.primaryButton.y + layout.primaryButton.height / 2,
      () => "全部获取并返回",
      {
        size: 21,
        align: "center",
        anchor: "center",
        bold: true
      }
    );
  }

  private drawTitle(renderer: DrawRenderer, layout: P0Layout): void {
    renderer.setColor("#12233d");
    renderer.fillArc(
      this.logicalWidth * 0.78,
      this.logicalHeight * 0.2,
      Math.min(this.logicalWidth, this.logicalHeight) * 0.24,
      0,
      Math.PI * 2
    );
    renderer.setColor("#365f8855");
    renderer.fillArc(
      this.logicalWidth * 0.2,
      this.logicalHeight * 0.72,
      Math.min(this.logicalWidth, this.logicalHeight) * 0.32,
      0,
      Math.PI * 2
    );
    renderer.setColor("#d7a866");
    renderer.fillLine(
      this.logicalWidth * 0.25,
      this.logicalHeight * 0.56,
      this.logicalWidth * 0.75,
      this.logicalHeight * 0.56
    );
    drawButton(renderer, layout.primaryButton);
  }

  private drawMap(
    renderer: DrawRenderer,
    layout: P0Layout,
    snapshot: ControllerSnapshot
  ): void {
    const map = this.content.maps[snapshot.state.mapId];
    if (!map) {
      return;
    }
    renderer.setColor("#101a2b");
    renderer.fillRect(
      layout.hud.x,
      layout.hud.y,
      layout.hud.width,
      layout.hud.height
    );
    drawPanel(renderer, layout.mapViewport, "#10192a", "#425d80");
    const viewport = layout.mapViewport;
    const defeated = new Set(snapshot.state.defeatedEncounters);
    for (let row = 0; row < viewport.rows; row += 1) {
      for (let column = 0; column < viewport.columns; column += 1) {
        const worldX = viewport.originX + column;
        const worldY = viewport.originY + row;
        if (worldX >= map.width || worldY >= map.height) {
          continue;
        }
        const index = worldY * map.width + worldX;
        const x = viewport.x + column * viewport.cellSize + 3;
        const y = viewport.y + row * viewport.cellSize + 3;
        renderer.setColor(
          map.blocked[index]
            ? "#182131"
            : (worldX + worldY) % 2 === 0
              ? "#263a55"
              : "#22334d"
        );
        renderer.fillRoundRect(
          x,
          y,
          viewport.cellSize - 6,
          viewport.cellSize - 6,
          9
        );
        if (!map.blocked[index] && (worldX * 7 + worldY * 3) % 11 === 0) {
          renderer.setColor("#6c93b933");
          renderer.fillArc(
            x + viewport.cellSize * 0.62,
            y + viewport.cellSize * 0.35,
            7,
            0,
            Math.PI * 2
          );
        }
      }
    }

    const cellCenter = (x: number, y: number) => ({
      x:
        viewport.x +
        (x - viewport.originX) * viewport.cellSize +
        viewport.cellSize / 2,
      y:
        viewport.y +
        (y - viewport.originY) * viewport.cellSize +
        viewport.cellSize / 2
    });
    for (const portal of map.portals) {
      if (
        portal.x < viewport.originX ||
        portal.y < viewport.originY ||
        portal.x >= viewport.originX + viewport.columns ||
        portal.y >= viewport.originY + viewport.rows
      ) {
        continue;
      }
      const center = cellCenter(portal.x, portal.y);
      renderer.setColor("#56d9d955");
      renderer.fillArc(center.x, center.y, 18, 0, Math.PI * 2);
      renderer.setColor("#8ffff0");
      renderer.strokeArc(center.x, center.y, 15, 0, Math.PI * 2);
    }
    for (const encounter of map.encounters) {
      if (
        defeated.has(`${map.mapId}:${encounter.instanceId}`) ||
        encounter.x < viewport.originX ||
        encounter.y < viewport.originY ||
        encounter.x >= viewport.originX + viewport.columns ||
        encounter.y >= viewport.originY + viewport.rows
      ) {
        continue;
      }
      const center = cellCenter(encounter.x, encounter.y);
      drawEnemy(renderer, center.x, center.y + 4, 0.33, false);
    }
    const player = cellCenter(
      snapshot.state.position.x,
      snapshot.state.position.y
    );
    drawHero(renderer, player.x, player.y + 5, 0.38);
    if (layout.mode === "landscape") {
      drawPanel(
        renderer,
        {
          x: viewport.x + viewport.width + 20,
          y: 82,
          width: this.logicalWidth - viewport.x - viewport.width - 44,
          height: this.logicalHeight - 106
        },
        "#121c2d"
      );
    }
    renderer.setColor("#172235");
    renderer.fillRoundRect(
      layout.menuButton.x,
      layout.menuButton.y,
      layout.menuButton.width,
      layout.menuButton.height,
      12
    );
  }

  private drawBattle(
    renderer: DrawRenderer,
    layout: P0Layout,
    snapshot: ControllerSnapshot
  ): void {
    const battle = snapshot.state.battle;
    renderer.setColor("#151226");
    renderer.fillRect(0, 0, this.logicalWidth, this.logicalHeight);
    renderer.setColor("#3c294955");
    renderer.fillArc(
      this.logicalWidth * 0.72,
      this.logicalHeight * 0.24,
      Math.min(this.logicalWidth, this.logicalHeight) * 0.28,
      0,
      Math.PI * 2
    );
    drawPanel(renderer, layout.enemyArea, "#171827cc", "#804e72");
    drawPanel(renderer, layout.playerArea, "#111c2dcc", "#456889");
    const enemyCenter = {
      x: layout.enemyArea.x + layout.enemyArea.width / 2,
      y: layout.enemyArea.y + layout.enemyArea.height * 0.62
    };
    const playerCenter = {
      x: layout.playerArea.x + layout.playerArea.width / 2,
      y: layout.playerArea.y + layout.playerArea.height * 0.62
    };
    drawEnemy(
      renderer,
      enemyCenter.x,
      enemyCenter.y,
      layout.mode === "portrait" ? 0.8 : 1,
      Boolean(battle?.won)
    );
    drawHero(
      renderer,
      playerCenter.x,
      playerCenter.y,
      layout.mode === "portrait" ? 0.72 : 0.9
    );
    if (battle) {
      drawHpBar(
        renderer,
        layout.enemyArea.x + 24,
        layout.enemyArea.y + 80,
        layout.enemyArea.width - 48,
        battle.enemyHp,
        battle.enemyMaxHp,
        "#dd5572"
      );
      drawHpBar(
        renderer,
        layout.playerArea.x + 24,
        layout.playerArea.y + 52,
        layout.playerArea.width - 48,
        snapshot.state.player.hp,
        snapshot.state.player.maxHp,
        "#4fb3e8"
      );
    }
    if (this.effectMs > 0 && battle && !battle.won) {
      renderer.setColor("#fff1b8");
      renderer.fillLine(
        enemyCenter.x - 42,
        enemyCenter.y + 34,
        enemyCenter.x + 42,
        enemyCenter.y - 38
      );
      renderer.fillLine(
        enemyCenter.x - 28,
        enemyCenter.y + 46,
        enemyCenter.x + 54,
        enemyCenter.y - 24
      );
    }
    drawButton(renderer, layout.primaryButton);
    renderer.setColor("#172235");
    renderer.fillRoundRect(
      layout.menuButton.x,
      layout.menuButton.y,
      layout.menuButton.width,
      layout.menuButton.height,
      12
    );
  }

  private drawResult(renderer: DrawRenderer, layout: P0Layout): void {
    renderer.setColor("#13233a");
    renderer.fillArc(
      this.logicalWidth / 2,
      this.logicalHeight * 0.4,
      Math.min(this.logicalWidth, this.logicalHeight) * 0.32,
      0,
      Math.PI * 2
    );
    drawPanel(
      renderer,
      layout.mode === "portrait"
        ? {
            x: 24,
            y: this.logicalHeight * 0.17,
            width: this.logicalWidth - 48,
            height: 360
          }
        : {
            x: this.logicalWidth / 2 - 280,
            y: 90,
            width: 560,
            height: 320
          },
      "#151c2cdd",
      "#d4a354"
    );
    renderer.setColor("#ffd375");
    renderer.fillArc(
      this.logicalWidth / 2,
      this.logicalHeight * 0.58,
      28,
      0,
      Math.PI * 2
    );
    drawButton(renderer, layout.primaryButton);
  }
}

class P0Stage extends Stage {
  private stageRoot: Container | undefined;
  private releaseProbe: (() => void) | undefined;
  private releaseStage: (() => void) | undefined;

  constructor(
    private readonly scene: SceneId,
    private readonly controller: GameControllerPort,
    private readonly content: ContentBundle,
    private readonly width: number,
    private readonly height: number,
    private readonly probe?: LifecycleProbePort
  ) {
    super();
    this.releaseStage = probe?.register("stage");
  }

  override onResetEvent(application: Application): void {
    this.forceDispose(application);
    this.stageRoot = new Container(0, 0, this.width, this.height);
    this.stageRoot.name = `p0-stage-${this.scene}`;
    this.releaseProbe = this.probe?.register("stageRoot");
    this.stageRoot.addChild(
      new SceneSurface(
        this.scene,
        this.controller,
        this.content,
        this.width,
        this.height,
        this.probe
      )
    );
    application.world.addChild(this.stageRoot, 0);
  }

  override onDestroyEvent(application: Application): void {
    this.forceDispose(application);
  }

  forceDispose(application: Application): void {
    if (this.stageRoot?.ancestor) {
      application.world.removeChildNow(this.stageRoot);
    }
    this.stageRoot = undefined;
    this.releaseProbe?.();
    this.releaseProbe = undefined;
  }

  disposePermanently(application: Application): void {
    this.forceDispose(application);
    this.releaseStage?.();
    this.releaseStage = undefined;
  }
}

const stateIds: Record<SceneId, number> = {
  title: state.USER + 21,
  map: state.USER + 22,
  battle: state.USER + 23,
  result: state.USER + 24
};

const actionForKeyboard = (
  event: KeyboardEvent,
  snapshot: ControllerSnapshot
): GameAction | null => {
  const key = event.key.toLowerCase();
  if (key === "arrowup" || key === "w") {
    return { type: "move", dx: 0, dy: -1 };
  }
  if (key === "arrowdown" || key === "s") {
    return { type: "move", dx: 0, dy: 1 };
  }
  if (key === "arrowleft" || key === "a") {
    return { type: "move", dx: -1, dy: 0 };
  }
  if (key === "arrowright" || key === "d") {
    return { type: "move", dx: 1, dy: 0 };
  }
  if (key === "escape") {
    return { type: "cancel" };
  }
  if (key === "m") {
    return { type: "open-menu" };
  }
  if (key === "enter" || key === " ") {
    if (snapshot.state.scene === "battle" && !snapshot.state.battle?.won) {
      return { type: "basic-attack" };
    }
    return { type: "confirm" };
  }
  return null;
};

export const createP0Runtime = async (
  options: P0RuntimeOptions
): Promise<MountedRuntime> => {
  const { target, controller, content, probe } = options;
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
  const portrait =
    window.innerHeight > window.innerWidth
    && window.matchMedia("(pointer: coarse)").matches;
  const width = portrait ? 390 : 960;
  const height = portrait ? 844 : 540;
  const releaseHost = probe?.register("runtimeHost");
  const application = new Application(width, height, {
    parent: target,
    renderer: video.CANVAS,
    scale: "auto",
    scaleMethod: "fit",
    physic: "none",
    antiAlias: true,
    consoleHeader: false,
    backgroundColor: "#090e19"
  });
  application.canvas.setAttribute("aria-label", "H5 RPG P0 本地试玩画布");
  application.canvas.style.touchAction = "none";
  const releaseResizeObserver = probe?.register("resizeObserver");
  const resizeObserver = new ResizeObserver(() => application.resize());
  resizeObserver.observe(target);
  application.resize();

  const stages = Object.fromEntries(
    (Object.keys(stateIds) as SceneId[]).map((scene) => [
      scene,
      new P0Stage(scene, controller, content, width, height, probe)
    ])
  ) as Record<SceneId, P0Stage>;
  for (const scene of Object.keys(stateIds) as SceneId[]) {
    state.set(stateIds[scene], stages[scene]);
  }

  let releaseUi: (() => void) | undefined;
  const mountRuntimeUi = (): void => {
    releaseUi = options.mountUi?.(application, {
      controller,
      content,
      width,
      height,
      ...(probe ? { probe } : {})
    });
  };
  let currentScene = controller.getSnapshot().state.scene;
  let currentWindow = controller.getSnapshot().topWindow;
  const historyMarker = `h5-rpg-window-${Date.now()}-${Math.random()}`;
  let ownsHistoryEntry = false;
  const onPopState = (): void => {
    if (!ownsHistoryEntry) {
      return;
    }
    ownsHistoryEntry = false;
    if (controller.getSnapshot().topWindow) {
      controller.dispatch({ type: "cancel" });
    }
  };
  window.addEventListener("popstate", onPopState);
  state.change(stateIds[currentScene], true);
  mountRuntimeUi();
  const unsubscribe = controller.subscribe((snapshot) => {
    if (!currentWindow && snapshot.topWindow) {
      const previousState =
        history.state && typeof history.state === "object"
          ? history.state
          : {};
      history.pushState(
        { ...previousState, __h5RpgWindow: historyMarker },
        ""
      );
      ownsHistoryEntry = true;
    } else if (
      currentWindow &&
      !snapshot.topWindow &&
      ownsHistoryEntry
    ) {
      const entryBelongsToRuntime =
        history.state?.__h5RpgWindow === historyMarker;
      ownsHistoryEntry = false;
      if (entryBelongsToRuntime) {
        history.back();
      }
    }
    currentWindow = snapshot.topWindow;
    if (snapshot.state.scene !== currentScene) {
      currentScene = snapshot.state.scene;
      releaseUi?.();
      releaseUi = undefined;
      state.change(stateIds[currentScene], true);
      mountRuntimeUi();
    }
  });

  const onKeyDown = (event: KeyboardEvent) => {
    const action = actionForKeyboard(event, controller.getSnapshot());
    if (action) {
      event.preventDefault();
      controller.dispatch(action);
    }
  };
  window.addEventListener("keydown", onKeyDown);

  let pointerStart: { x: number; y: number } | undefined;
  const logicalPoint = (event: PointerEvent) => {
    const bounds = application.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * width,
      y: ((event.clientY - bounds.top) / bounds.height) * height
    };
  };
  const onPointerDown = (event: PointerEvent) => {
    pointerStart = logicalPoint(event);
    application.canvas.setPointerCapture?.(event.pointerId);
  };
  const onPointerUp = (event: PointerEvent) => {
    const end = logicalPoint(event);
    const start = pointerStart ?? end;
    pointerStart = undefined;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) >= 30) {
      controller.dispatch(
        Math.abs(dx) > Math.abs(dy)
          ? { type: "move", dx: dx > 0 ? 1 : -1, dy: 0 }
          : { type: "move", dx: 0, dy: dy > 0 ? 1 : -1 }
      );
      return;
    }
    const action = controller.resolvePoint(end.x, end.y, width, height);
    if (action) {
      controller.dispatch(action);
    }
  };
  application.canvas.addEventListener("pointerdown", onPointerDown);
  application.canvas.addEventListener("pointerup", onPointerUp);

  return {
    getLogicalSize: () => ({ width, height }),
    async dispose(): Promise<void> {
      unsubscribe();
      resizeObserver.disconnect();
      releaseResizeObserver?.();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("popstate", onPopState);
      application.canvas.removeEventListener("pointerdown", onPointerDown);
      application.canvas.removeEventListener("pointerup", onPointerUp);
      if (
        ownsHistoryEntry &&
        history.state?.__h5RpgWindow === historyMarker
      ) {
        ownsHistoryEntry = false;
        history.back();
      }
      state.stop();
      releaseUi?.();
      releaseUi = undefined;
      state.set(state.DEFAULT, new Stage());
      state.change(state.DEFAULT, true);
      state.stop();
      for (const scene of Object.keys(stateIds) as SceneId[]) {
        stages[scene].disposePermanently(application);
        state.set(stateIds[scene], new Stage());
      }
      application.destroy(true);
      releaseHost?.();
      await Promise.resolve();
    }
  };
};

export interface RpgInteractiveSurface extends UIBaseElement {
  handleTap(x: number, y: number): boolean;
  handleSwipe(deltaX: number, deltaY: number): boolean;
  handleWheel?(deltaY: number): boolean;
  handleKey(key: string): boolean;
}

export interface RpgRuntimeOptions<TSurface extends RpgInteractiveSurface> {
  target: HTMLElement;
  desktopSize: { width: number; height: number };
  portraitSize: { width: number; height: number };
  portraitBreakpoint?: number;
  ariaLabel: string;
  backgroundColor?: string;
  createSurface(width: number, height: number): TSurface;
}

export interface MountedRpgRuntime<TSurface extends RpgInteractiveSurface> {
  application: Application;
  surface: TSurface;
  getLogicalSize(): { width: number; height: number };
  dispose(): Promise<void>;
}

class RpgApplicationStage<TSurface extends RpgInteractiveSurface> extends Stage {
  private root: Container | undefined;
  private surfaceValue: TSurface | undefined;

  constructor(
    private readonly width: number,
    private readonly height: number,
    private readonly createSurfaceValue: (
      width: number,
      height: number
    ) => TSurface
  ) {
    super();
  }

  get surface(): TSurface {
    if (!this.surfaceValue) {
      throw new Error("RPG surface has not been initialized");
    }
    return this.surfaceValue;
  }

  override onResetEvent(application: Application): void {
    this.forceDispose(application);
    this.root = new Container(0, 0, this.width, this.height);
    this.root.name = "h5-rpg-application-stage";
    this.surfaceValue = this.createSurfaceValue(this.width, this.height);
    this.root.addChild(this.surfaceValue);
    application.world.addChild(this.root);
  }

  override onDestroyEvent(application: Application): void {
    this.forceDispose(application);
  }

  forceDispose(application: Application): void {
    if (this.root?.ancestor) {
      application.world.removeChildNow(this.root);
    }
    this.root = undefined;
    this.surfaceValue = undefined;
  }
}

/**
 * Framework-owned host for complete RPGs.
 *
 * Games provide one interactive surface; the framework owns melonJS
 * Application/Stage creation, responsive design size, input routing and
 * lifecycle cleanup.
 */
export const mountRpgRuntime = async <
  TSurface extends RpgInteractiveSurface
>(
  options: RpgRuntimeOptions<TSurface>
): Promise<MountedRpgRuntime<TSurface>> => {
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
  const portrait =
    window.innerHeight > window.innerWidth
    && window.innerWidth <= (options.portraitBreakpoint ?? 720);
  const { width, height } = portrait
    ? options.portraitSize
    : options.desktopSize;
  const application = new Application(width, height, {
    parent: options.target,
    renderer: video.CANVAS,
    scale: "auto",
    scaleMethod: "fit",
    physic: "none",
    antiAlias: true,
    consoleHeader: false,
    backgroundColor: options.backgroundColor ?? "#090e19"
  });
  application.canvas.setAttribute("aria-label", options.ariaLabel);
  application.canvas.dataset.designWidth = String(width);
  application.canvas.dataset.designHeight = String(height);
  application.canvas.style.maxWidth = `${width}px`;
  application.canvas.style.maxHeight = `${height}px`;
  application.canvas.style.touchAction = "none";
  application.canvas.tabIndex = 0;

  const resizeObserver = new ResizeObserver(() => application.resize());
  resizeObserver.observe(options.target);
  application.resize();

  const stateId = state.USER + 71;
  const stage = new RpgApplicationStage(
    width,
    height,
    options.createSurface
  );
  state.set(stateId, stage);
  state.change(stateId, true);

  const logicalPoint = (event: PointerEvent) => {
    const bounds = application.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * width,
      y: ((event.clientY - bounds.top) / bounds.height) * height
    };
  };
  let pointerStart: { x: number; y: number } | undefined;
  const onPointerDown = (event: PointerEvent): void => {
    pointerStart = logicalPoint(event);
    application.canvas.setPointerCapture?.(event.pointerId);
  };
  const onPointerUp = (event: PointerEvent): void => {
    const end = logicalPoint(event);
    const start = pointerStart ?? end;
    pointerStart = undefined;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (!stage.surface.handleSwipe(dx, dy)) {
      stage.surface.handleTap(end.x, end.y);
    }
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (stage.surface.handleKey(event.key)) {
      event.preventDefault();
    }
  };
  const onWheel = (event: WheelEvent): void => {
    if (stage.surface.handleWheel?.(event.deltaY)) {
      event.preventDefault();
    }
  };
  const onContextMenu = (event: MouseEvent): void => event.preventDefault();
  application.canvas.addEventListener("pointerdown", onPointerDown);
  application.canvas.addEventListener("pointerup", onPointerUp);
  application.canvas.addEventListener("wheel", onWheel, { passive: false });
  application.canvas.addEventListener("contextmenu", onContextMenu);
  window.addEventListener("keydown", onKeyDown);

  let disposed = false;
  return {
    application,
    surface: stage.surface,
    getLogicalSize: () => ({ width, height }),
    async dispose(): Promise<void> {
      if (disposed) return;
      disposed = true;
      resizeObserver.disconnect();
      application.canvas.removeEventListener("pointerdown", onPointerDown);
      application.canvas.removeEventListener("pointerup", onPointerUp);
      application.canvas.removeEventListener("wheel", onWheel);
      application.canvas.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown);
      state.stop();
      stage.forceDispose(application);
      state.set(stateId, new Stage());
      application.destroy(true);
      await Promise.resolve();
    }
  };
};
